import { Card, Spacer, useCurrentMode } from '@lokalise/louis';

import MainSpinner from '@app/components/MainSpinner';
import Promo from '@app/components/Promo';
import Tutorial from '@app/components/Tutorial';
import useSelector from '@app/hooks/useSelector';
import asset from '@app/utils/asset';

import { ProjectType } from '../../../../types/project';
import { getFileFormats } from '../../reducers/downloadData/selectors';
import type {
    AvailableProjects,
    ExportConfig,
    FileFormat,
    Indentation,
    PlaceholderFormat,
    PluralFormat,
} from '../../types';
import CustomFileAndFolderStructure from '../CustomFileAndFolderStructure';
import ExportButtons from '../ExportButtons';
import FileFormatToDownload from '../FileFormatToDownload';
import FilterData from '../FilterData';
import Languages from '../Languages';
import Options from '../Options';
import IncludeOtherProjectStrings from '../OtherProjectsStrings';
import QaAlert from '../QaAlert';
import Triggers from '../Triggers';
import Webhooks from '../Webhooks';
import useDownloadReducer from './reducer';
import { Layout, QuickDownloadTitle, SidePageWrap } from './styles';
import { isCustomFileAndFolderStructureVisible, isTriggerVisible } from './utils/visibilityHelpers';

interface DownloadProps {
    exportConfig: ExportConfig;
    placeholderFormatDefaults: Partial<Record<FileFormat, PlaceholderFormat>>;
    pluralFormatDefaults: Partial<Record<FileFormat, PluralFormat>>;
    indentationDefaults: Partial<Record<FileFormat, Indentation>>;
    projectType: ProjectType;
    availableProjects: AvailableProjects;
    loading: boolean;
    projectId: string;
    settingsPermissionsEnabled: boolean;
}

const DownloadPage = ({
    exportConfig,
    placeholderFormatDefaults,
    pluralFormatDefaults,
    indentationDefaults,
    projectType,
    availableProjects,
    loading,
    projectId,
    settingsPermissionsEnabled,
}: DownloadProps) => {
    const darkmode = useCurrentMode() === 'dark';
    const fileFormats = useSelector(getFileFormats);
    const defaultFileFormat = fileFormats.some((formatGroup) =>
        formatGroup.formats.some((format) => format.value === exportConfig.fileFormat),
    )
        ? exportConfig.fileFormat
        : fileFormats[0].formats[0].value;

    const updatedExportConfig = {
        ...exportConfig,
        defaultFileFormat,
    };
    const [values, dispatch] = useDownloadReducer(updatedExportConfig);

    const { triggersGithub, triggersAzure, triggersBitbucket, triggersGitlab } = values;

    const gitIntegrationEnabled = [triggersGithub, triggersAzure, triggersBitbucket, triggersGitlab].some(
        (value) => value,
    );

    return (
        <Layout>
            {loading && <MainSpinner />}
            <div>
                <Spacer bottom={4}>
                    <QaAlert />
                </Spacer>
                <Spacer>
                    <FileFormatToDownload
                        value={{
                            fileFormat: values.fileFormat,
                            switchIncludeAll: values.switchIncludeAll,
                        }}
                        projectType={projectType}
                        projectId={projectId}
                        onChange={(value) => {
                            dispatch.updateFileFormat({
                                value,
                                pluralFormatDefaults,
                                placeholderFormatDefaults,
                                indentationDefaults,
                                exportConfig,
                            });
                        }}
                    />
                </Spacer>
                <Spacer top={2}>
                    <Languages
                        value={{
                            languages: values.exportLangs,
                            customIso: values.customIsoCodes?.[values.fileFormat] ?? {},
                        }}
                        projectId={projectId}
                        onChange={dispatch.updateLanguageList}
                    />
                </Spacer>
                {isCustomFileAndFolderStructureVisible(values.fileFormat, projectType) && (
                    <Spacer top={2}>
                        <CustomFileAndFolderStructure
                            value={{
                                filenamesCustom: values.filenamesCustom,
                                filenamesOriginal: values.filenamesOriginal,
                                zipStructure: values.zipStructure,
                                directoryPrefix: values.directoryPrefix,
                            }}
                            fileFormat={values.fileFormat}
                            projectId={projectId}
                            includeAllPlatformKeys={values.switchIncludeAll}
                            onChange={dispatch.updateFileAndFormatStructure}
                        />
                    </Spacer>
                )}
                <Spacer top={2}>
                    <FilterData
                        value={{
                            includeTags: values.includeTags,
                            excludeTags: values.excludeTags,
                            filterFilenames: values.filterFilenames,
                            customTranslationStatusIds: values.customTranslationStatusIds,
                            exportAll: values.exportAll,
                            exportGlastReviewed: values.exportGlastReviewed,
                            exportGnonfuzzy: values.exportGnonfuzzy,
                            exportGnonhidden: values.exportGnonhidden,
                            exportGproofread: values.exportGproofread,
                            exportGtranslated: values.exportGtranslated,
                            exportGuntranslated: values.exportGuntranslated,
                        }}
                        fileFormat={values.fileFormat}
                        projectType={projectType}
                        projectId={projectId}
                        switchIncludeAll={values.switchIncludeAll}
                        onChange={dispatch.updateFilterData}
                    />
                </Spacer>
                <Spacer top={2}>
                    <Options
                        value={{
                            switchCompact: values.switchCompact,
                            switchIncludeDescription: values.switchIncludeDescription,
                            switchIncludeComments: values.switchIncludeComments,
                            switchNoLanguageFolders: values.switchNoLanguageFolders,
                            switchDisableReferences: values.switchDisableReferences,
                            switchYamlIncludeRoot: values.switchYamlIncludeRoot,
                            jsonUnescapedSlashes: values.jsonUnescapedSlashes,
                            addNewlineEof: values.addNewlineEof,
                            switchIcuNumeric: values.switchIcuNumeric,
                            replaceLineBreaks: !!values.replaceLineBreaks[values.fileFormat],
                            exportEmpty: values.exportEmpty,
                            exportNullAs: values.exportNullAs,
                            exportSort: values.exportSort,
                            pluralFormat: values.pluralFormat,
                            placeholderFormat: values.placeholderFormat,
                            switchEscapePercent: values.switchEscapePercent,
                            indentation: values.indentation,
                            javaPropertiesSeparator: values.javaPropertiesSeparator,
                            javaPropertiesEncoding: values.javaPropertiesEncoding,
                        }}
                        fileFormat={values.fileFormat}
                        projectType={projectType}
                        projectId={projectId}
                        isNoLanguageFoldersSwitchDisabled={!values.filenamesOriginal}
                        onChange={dispatch.updateOptions}
                    />
                </Spacer>
                {projectType.toString() === ProjectType.localizationFiles && availableProjects.length > 0 && (
                    <Spacer top={2}>
                        <IncludeOtherProjectStrings
                            value={values.exportPids}
                            projectId={projectId}
                            onChange={(exportPids) => dispatch.updateOtherProjectsStrings({ exportPids })}
                        />
                    </Spacer>
                )}
                {isTriggerVisible(values.fileFormat, projectType) && (
                    <Spacer top={2}>
                        <Triggers
                            value={{
                                triggers: {
                                    triggersAmazons3: values.triggersAmazons3,
                                    triggersGcs: values.triggersGcs,
                                    triggersGithub: values.triggersGithub,
                                    triggersGithubEnterprise: values.triggersGithubEnterprise,
                                    triggersGitlab: values.triggersGitlab,
                                    triggersBitbucket: values.triggersBitbucket,
                                    triggersBitbucketEnterprise: values.triggersBitbucketEnterprise,
                                    triggersAzure: values.triggersAzure,
                                },
                                filterRepositories: values.filterRepositories,
                                commitTemplate: values.commitTemplate,
                                defaultBitbucketReviewers: values.defaultBitbucketReviewers,
                            }}
                            projectId={projectId}
                            onChange={dispatch.updateTriggers}
                        />
                    </Spacer>
                )}
                <Spacer top={2}>
                    <Webhooks
                        value={{
                            switchWebhooks: values.switchWebhooks,
                            webhooks: values.webhooks,
                        }}
                        projectId={projectId}
                        onChange={dispatch.updateWebhooks}
                    />
                </Spacer>
                <Spacer top={6} data-metric="export-footer-buttons">
                    <ExportButtons values={values} mode="default" gitIntegrationEnabled={gitIntegrationEnabled} />
                </Spacer>
            </div>
            <SidePageWrap data-metric="export-side-buttons">
                <Card>
                    <Spacer bottom={2}>
                        <QuickDownloadTitle>Quick download with current settings</QuickDownloadTitle>
                    </Spacer>
                    <ExportButtons values={values} mode="quick" gitIntegrationEnabled={gitIntegrationEnabled} />
                </Card>
                <Tutorial videoHash="4uj669mxak" subtitle="Downloading files" />
                {settingsPermissionsEnabled && (
                    <Promo
                        icons={[
                            {
                                filename: asset(`/img/integrations/github${darkmode ? '_darkmode' : ''}.svg`),
                                alt: 'Integration with Github',
                            },
                            { filename: asset('/img/integrations/bitbucket.svg'), alt: 'Integration with Bitbucket' },
                            { filename: asset('/img/integrations/api.png'), alt: 'Integration with API' },
                        ]}
                        title="Export automatically"
                        subtitle="See app integrations"
                        href={`/apps/${projectId}/`}
                    />
                )}
            </SidePageWrap>
        </Layout>
    );
};

export default DownloadPage;
